import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import Collapse from "@mui/material/Collapse";

import useGetPortfolioPlanByIdPublic from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioPlanByIdPublic";
import { AssetClassId } from "@fartherfinance/frontend/api/Investment/types";
import useSecuritiesBySecurityIds from "@fartherfinance/frontend/api/SecurityMaster/hooks/useSecuritiesBySecurityIds";
import {
  PortfolioPlanId,
  SecurityId,
  Ticker,
} from "@fartherfinance/frontend/api/Types";

import {
  EXPAND_COLLAPSE_CHILDREN,
  REASSIGN_SLEEVE_OR_SECURITY_IN_MODEL,
  REMOVE_ASSET_CLASS_OR_SLEEVE_OR_SECURITY_FROM_MODEL,
  SET_LOCKED,
  SET_SLEEVE_NAME,
  SET_SLEEVES_AND_SECURITIES,
  SET_WEIGHT,
  UPDATE_CUSTOM_ASSET_CLASS,
} from "../../PortfolioBuilder/reducer/actions";
import { usePortfolioBuilderContext } from "../../PortfolioBuilder/reducer/Context";
import {
  AssetCategoryNotCashAndClass,
  AssetClassCategory,
  Children,
  ElementID,
  ModelTableRowType,
} from "../../PortfolioBuilder/reducer/Types";
import CreateEditAssetClassModal from "../../PortfolioBuilder/SearchBarAndTableConstructor/CreateEditAssetClassModal/CreateEditAssetClassModal";
import ReAssignAssetClassModal from "../../ReAssignAssetClassModal/ReAssignAssetClassModal";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import { toClassName } from "@src/multiCustodian/utils/to-class-name";
import Chip from "@src/sharedComponents/Chip/Chip";
import CircularProgressLoader from "@src/sharedComponents/CircularProgressLoader/CircularProgressLoader";
import ThreeDotsPopoverMenu from "@src/sharedComponents/Popover/ThreeDotsPopoverMenu/Menu";
import MenuItem from "@src/sharedComponents/Popover/ThreeDotsPopoverMenu/MenuItem";
import Text from "@src/sharedComponents/Typography/Text";
import { assertUnreachable } from "@src/utils/assertUnreachable";

import Lock from "./Lock/Lock";
import SecurityRow, { Space3 } from "./SecurityRow";
import WeightInput from "./WeightInput/WeightInput";

import styles from "./TableRow.module.css";

export const TEMPORARY_SLEEVE_NAME = "-"; // see action: SET_SLEEVE_NAME in reducer.ts for explanation

interface Props {
  id: ElementID;
  type: Extract<ModelTableRowType, "AssetClass" | "Sleeve">;
  name: string;
  assetClassCategory: AssetClassCategory;
  assetClassId: AssetClassId;
  weight: string;
  isLocked: boolean;
  elementChildren: Children;
  level: number; // nesting within the model/table
  isInvisible: boolean;
  portfolioPlanId?: PortfolioPlanId;
  expand: boolean;
  isInInvisibleAssetClass: boolean;
  isInSleeve: boolean;
}

const ParentRow: React.FC<Props> = ({
  id,
  type,
  name,
  assetClassCategory,
  assetClassId,
  weight,
  isLocked,
  elementChildren,
  level,
  isInvisible,
  portfolioPlanId = null,
  expand,
  isInInvisibleAssetClass,
  isInSleeve,
}) => {
  const [showEditAssetClassModal, setShowEditAssetClassModal] =
    useState<boolean>(false);
  const [showPopover, setShowPopover] = useState<boolean>(false);

  const onShowPopover = () => setShowPopover(true);
  const onHidePopover = () => setShowPopover(false);

  const [showReAssignModal, setShowReAssignModal] = useState<boolean>(false);
  const onShowReAssignModal = () => {
    setShowPopover(false);
    setShowReAssignModal(true);
  };
  const onHideReAssignModal = () => setShowReAssignModal(false);

  const anchorRef = useRef<HTMLDivElement | null>(null);

  const auth = useAdvisorRequestAuth();
  const portfolioPlan = useGetPortfolioPlanByIdPublic(
    portfolioPlanId,
    {},
    auth
  );

  const securityIdsInPortfolioPlan = useMemo(() => {
    if (portfolioPlan.data !== undefined) {
      return portfolioPlan.data.data.plan.entries
        .filter((el) => el.type === "Security")
        .map((el) => el.securityId);
    }

    return null;
  }, [portfolioPlan.data]);

  const securitiesInPortfolioPlan = useSecuritiesBySecurityIds(
    securityIdsInPortfolioPlan,
    auth
  );

  const { dispatch } = usePortfolioBuilderContext();

  useEffect(() => {
    if (portfolioPlan.data !== undefined && name === TEMPORARY_SLEEVE_NAME) {
      dispatch({
        type: SET_SLEEVE_NAME,
        payload: {
          elementId: id,
          name: portfolioPlan.data.data.plan.name,
        },
      });
    }
  }, [portfolioPlan.data, id, dispatch, name]);

  useEffect(() => {
    if (
      elementChildren.length === 0 &&
      portfolioPlan.data !== undefined &&
      securitiesInPortfolioPlan.data !== undefined
    ) {
      const securities = portfolioPlan.data.data.plan.entries.filter(
        (el) => el.type === "Security"
      );
      const secLookup = securities.reduce((acc, sec) => {
        return {
          ...acc,
          [sec.securityId]: sec.weight.mul(100).toNumber(),
        };
      }, {} as { [key: SecurityId]: number });

      const sleeves = portfolioPlan.data.data.plan.entries.filter(
        (el) => el.type === "NestedPlan"
      );

      const secs = securitiesInPortfolioPlan.data.map((el) => {
        return {
          securityId: el.id,
          ticker:
            (el.identifiers.find((i) => i.registrar === "TICKER")
              ?.identifier as Ticker) ?? ("N/A" as Ticker),
          description: el.description ?? "N/A",
          weight: `${secLookup[el.id]}`,
        };
      });

      dispatch({
        type: SET_SLEEVES_AND_SECURITIES,
        payload: {
          elementId: id,
          sleeves: sleeves.map((el) => ({
            name: TEMPORARY_SLEEVE_NAME, // see action: SET_SLEEVE_NAME in reducer.ts for explanation
            portfolioPlanId: el.planId,
            weight: el.weight.mul(100).toNumber().toString(),
          })),
          securities: secs,
        },
      });
    }
  }, [
    portfolioPlan,
    id,
    portfolioPlanId,
    securitiesInPortfolioPlan,
    dispatch,
    elementChildren,
  ]);

  const onChangeWeight = (newWeight: string) =>
    dispatch({
      type: SET_WEIGHT,
      payload: {
        id,
        weight: newWeight,
        assetClassId,
      },
    });

  const onClickLock = () =>
    dispatch({
      type: SET_LOCKED,
      payload: {
        id,
        isLocked: !isLocked,
      },
    });

  const onCloseEditAssetClassModal = () => setShowEditAssetClassModal(false);

  const onEditAssetClass = () => {
    setShowPopover(false);
    setShowEditAssetClassModal(true);
  };

  const onReAssign = (
    elementId: ElementID,
    fromAssetClassId: AssetClassId,
    toAssetClassElementId: ElementID
  ) =>
    dispatch({
      type: REASSIGN_SLEEVE_OR_SECURITY_IN_MODEL,
      payload: {
        elementId,
        fromAssetClassId,
        toAssetClassElementId,
        weight,
      },
    });

  const onRemoveAssetClassOrSleeve = () =>
    dispatch({
      type: REMOVE_ASSET_CLASS_OR_SLEEVE_OR_SECURITY_FROM_MODEL,
      payload: {
        elementId: id,
        assetClassId,
        elementType: type,
        weight,
      },
    });

  const updateCustomAssetClassInBaseList = useCallback(
    (assetCategoryNotCashWithNewCustomClass: AssetCategoryNotCashAndClass) => {
      if (assetClassCategory === "Cash Equivalents") {
        throw new Error("assetClassCategory should not be: Cash Equivalents");
      }

      dispatch({
        type: UPDATE_CUSTOM_ASSET_CLASS,
        payload: {
          currentCategoryAndClass: {
            assetClassId: assetClassId,
            assetClassName: name,
            category: assetClassCategory,
          },
          newCategoryAndClass: assetCategoryNotCashWithNewCustomClass,
        },
      });

      setShowEditAssetClassModal(false);
    },
    [
      dispatch,
      setShowEditAssetClassModal,
      assetClassId,
      name,
      assetClassCategory,
    ]
  );

  const onExpand = useCallback(() => {
    dispatch({
      type: EXPAND_COLLAPSE_CHILDREN,
      payload: {
        elementId: id,
        expand: !expand,
      },
    });
  }, [id, dispatch, expand]);

  return (
    <>
      {showEditAssetClassModal && assetClassCategory !== "Cash Equivalents" && (
        <CreateEditAssetClassModal
          title={"Edit asset class"}
          isOpen={showEditAssetClassModal}
          onClose={onCloseEditAssetClassModal}
          onSubmit={updateCustomAssetClassInBaseList}
          assetClassCategory={assetClassCategory}
          assetClass={name}
        />
      )}

      {showReAssignModal && (
        <ReAssignAssetClassModal
          isOpen={showReAssignModal}
          onClose={onHideReAssignModal}
          onSubmit={onReAssign}
          element={{
            id,
            type: "Sleeve" as Extract<ModelTableRowType, "Sleeve">,
            name,
          }}
          currentAssetClassId={assetClassId}
        />
      )}

      {isInvisible === false && (
        <div
          className={toClassName(styles.row, {
            [styles.securityOrSleeveInInvisibleAssetClass]:
              isInInvisibleAssetClass && level === 0,
          })}
        >
          <div className={styles.flexRow}>
            <div
              className={styles.takeUpSpace}
              style={{ width: `${Space3 * level}px` }}
            />

            <ArrowRightIcon
              className={toClassName(styles.arrow, {
                [styles.arrowRotateDown]: expand,
                [styles.arrowRotateRight]: !expand,
              })}
              onClick={onExpand}
            />

            <Text
              className={styles.assetClassLabel}
              variant="body"
              color={"textSubtle"}
            >
              {name}
            </Text>
          </div>

          <div className={styles.flexRow}>
            <div className={styles.chipSpacer}>
              <Chip
                variant="outlined"
                label={type === "AssetClass" ? "Asset Class" : "Sleeve"}
              />
            </div>

            {isInSleeve ? (
              <div className={styles.disabledWeightTextContainer}>
                <Text
                  className={styles.disabledWeightText}
                  variant="body"
                  color={"textSubtle"}
                >
                  {`${weight} %`}
                </Text>
              </div>
            ) : type === "Sleeve" ? (
              <WeightInput value={weight} onChange={onChangeWeight} />
            ) : (
              <Text
                className={styles.disabledAssetClassWeightText}
                variant="body"
                color={"textSubtle"}
              >
                {`${weight} %`}
              </Text>
            )}

            {false && <Lock isLocked={isLocked} onPress={onClickLock} />}

            {level <= 1 && isInSleeve === false ? (
              <ThreeDotsPopoverMenu
                className={styles.threeDotsPopoverMenu}
                anchorRef={anchorRef}
                isOpen={showPopover}
                onOpen={onShowPopover}
                onClose={onHidePopover}
              >
                {assetClassCategory !== "Cash Equivalents" &&
                  type === "AssetClass" && (
                    <MenuItem
                      className={styles.popoverMenuItem}
                      onClick={onEditAssetClass}
                    >
                      Edit asset class
                    </MenuItem>
                  )}

                {assetClassCategory !== "Cash Equivalents" &&
                  type === "Sleeve" && (
                    <MenuItem
                      className={styles.popoverMenuItem}
                      onClick={onShowReAssignModal}
                    >
                      Re-assign asset class
                    </MenuItem>
                  )}

                <MenuItem
                  className={styles.popoverMenuItem}
                  onClick={onRemoveAssetClassOrSleeve}
                >
                  Remove
                </MenuItem>
              </ThreeDotsPopoverMenu>
            ) : (
              <div
                className={styles.takeUpSpace}
                style={{ width: `${48}px` }}
              />
            )}
          </div>
        </div>
      )}

      <Collapse orientation="vertical" in={expand} collapsedSize={0}>
        {elementChildren.map((child) => {
          switch (child.type) {
            case "Security":
              return (
                <SecurityRow
                  key={child.id}
                  id={child.id}
                  ticker={child.ticker}
                  description={child.description}
                  weight={child.weight}
                  isLocked={child.isLocked}
                  level={level + 1}
                  isInSleeve={type === "Sleeve"}
                  assetClassCategory={assetClassCategory}
                  assetClassId={assetClassId}
                  isInInvisibleAssetClass={isInInvisibleAssetClass}
                />
              );
            case "Sleeve":
              return (
                <ParentRow
                  key={child.id}
                  id={child.id}
                  type={"Sleeve"}
                  name={child.name}
                  weight={child.weight}
                  isLocked={child.isLocked}
                  elementChildren={child.children}
                  level={level + 1}
                  isInvisible={false}
                  assetClassCategory={assetClassCategory}
                  assetClassId={assetClassId}
                  portfolioPlanId={child.portfolioPlanId}
                  expand={child.expand}
                  isInInvisibleAssetClass={isInInvisibleAssetClass}
                  isInSleeve={type === "Sleeve"}
                />
              );
            default:
              return assertUnreachable(child);
          }
        })}

        {elementChildren.length === 0 && (
          <>
            {type === "Sleeve" ? (
              <div className={styles.row}>
                <div className={styles.flexRow}>
                  <div
                    className={styles.takeUpSpace}
                    style={{ width: `${Space3 * level}px` }}
                  />

                  <Text
                    className={styles.noChildrenText}
                    variant="body"
                    color={"textDisabled"}
                  >
                    {portfolioPlan.hasError
                      ? "Error retrieving portfolio"
                      : "Loading..."}
                  </Text>

                  <CircularProgressLoader
                    className={styles.loader}
                    isLoading={portfolioPlan.isLoading}
                    innerWidthHeight={14}
                    outerWidthHeight={20}
                  />
                </div>
              </div>
            ) : isInvisible === false ? (
              <div className={styles.row}>
                <div className={styles.flexRow}>
                  <div
                    className={styles.takeUpSpace}
                    style={{ width: `${Space3 * level}px` }}
                  />
                  <Text
                    className={styles.noChildrenText}
                    variant="body"
                    color={"textDisabled"}
                  >
                    {type === "AssetClass"
                      ? "No holdings in this asset class"
                      : "No holdings in this sleeve"}
                  </Text>

                  <CircularProgressLoader
                    className={styles.loader}
                    isLoading={false}
                    innerWidthHeight={14}
                    outerWidthHeight={20}
                  />
                </div>
              </div>
            ) : undefined}
          </>
        )}
      </Collapse>
    </>
  );
};

export default ParentRow;
